import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned';

const extractBasicInfo = (currentSku = {}, productDetails = {}) => ({
    badgeAltText: currentSku?.badgeAltText,
    biExclusiveLevel: currentSku?.biExclusiveLevel,
    brandName: currentSku?.brandName || productDetails?.brand?.displayName,
    starRatings: productDetails?.rating,
    size: currentSku?.size,
    productReviewCount: productDetails?.reviews,
    image: currentSku?.skuImages?.imageUrl || currentSku?.smallImage
});

const extractFlags = (currentSku = {}) => ({
    isAppExclusive: currentSku?.isAppExclusive,
    isBiOnly: currentSku?.isBiOnly,
    isFirstAccess: currentSku?.isFirstAccess,
    isFree: currentSku?.isFree,
    isLimitedEdition: currentSku?.isLimitedEdition,
    isLimitedQuantity: currentSku?.isOnlyFewLeft,
    isLimitedTimeOffer: currentSku?.isLimitedTimeOffer,
    isNew: currentSku?.isNew,
    isOnlineOnly: currentSku?.isOnlineOnly,
    isOutOfStock: currentSku?.isOutOfStock,
    isSephoraExclusive: currentSku?.isSephoraExclusive
});

const extractProductInfo = (currentSku = {}, productDetails = {}, sourceData = {}) => ({
    listPrice: currentSku?.listPrice,
    salePrice: currentSku?.salePrice,
    valuePrice: currentSku?.valuePrice,
    wholeSalePrice: currentSku?.wholeSalePrice,
    productId: sourceData?.productId,
    productName: productDetails?.displayName || currentSku?.productName,
    skuId: currentSku?.skuId,
    skuImages: {
        imageUrl: currentSku?.skuImages?.imageUrl
    },
    smallImage: currentSku?.smallImage,
    targetUrl: currentSku?.targetUrl
});

const extractVariationInfo = (currentSku = {}) => ({
    variationId: currentSku?.skuId,
    variationType: currentSku?.variationValue,
    variationTypeDisplayName: currentSku?.variationTypeDisplayName,
    variationValue: currentSku?.variationValue
});

const extractSentiments = (sourceData = {}) => {
    const sentiments = sourceData?.sentiments || [];

    if (!Array.isArray(sentiments)) {
        return [];
    }

    return sentiments.slice(0, 3).map(sentiment => ({
        sentiment: sentiment?.sentiment,
        source: sentiment?.source,
        count: sentiment?.count,
        type: sentiment?.type
    }));
};

const extractFacets = (currentSku = {}) => {
    const skuRefinements = currentSku?.skuRefinements || [];

    if (!Array.isArray(skuRefinements)) {
        return [];
    }

    return skuRefinements.map(refinement => ({
        name: refinement?.key,
        values: refinement?.values
    }));
};

const transformProductDataToRecsServiceFormat = (sourceData = {}) => {
    const currentSku = sourceData?.currentSku || {};
    const productDetails = sourceData?.productDetails || {};

    return {
        ...extractBasicInfo(currentSku, productDetails),
        ...extractFlags(currentSku),
        ...extractProductInfo(currentSku, productDetails, sourceData),
        ...extractVariationInfo(currentSku),
        facets: extractFacets(currentSku),
        sentiments: extractSentiments(sourceData),
        strategyId: 'bestsellers'
    };
};

const getPdpCompareAttributes = masterlistWorldKey => {
    if (!masterlistWorldKey) {
        return [];
    }

    try {
        if (Sephora.configurationSettings?.recConfig?.slice) {
            //String is coming with extra quotes, removing them
            const fixedString = Sephora.configurationSettings.recConfig.slice(1, -1);
            const recommendationsConfiguration = JSON.parse(fixedString);

            if (Array.isArray(recommendationsConfiguration)) {
                const pdpCompareConfig = recommendationsConfiguration.find(config => config.key === masterlistWorldKey)?.pdpCompareAttrs;

                if (Array.isArray(pdpCompareConfig)) {
                    return pdpCompareConfig;
                }

                return [];
            }
        }

        return [];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing recommendation configuration:', error);

        return [];
    }
};

const findRefinementInMasterList = refinementKey => {
    const masterList = bpRedesignedUtils.getMasterListRefinementWorlds();

    if (!masterList || !Array.isArray(masterList)) {
        return null;
    }

    const matchingRefinements = [];

    for (const world of masterList) {
        const refinement = world?.refinements?.find(r => r.key === refinementKey);

        if (refinement) {
            matchingRefinements.push(refinement);
        }
    }

    if (matchingRefinements.length === 0) {
        return null;
    }

    if (matchingRefinements.length === 1) {
        return matchingRefinements[0];
    }

    const mergedRefinement = {
        ...matchingRefinements[0],
        items: []
    };

    const itemsMap = new Map();
    matchingRefinements.forEach(refinement => {
        refinement?.items?.forEach(item => {
            if (item?.key && !itemsMap.has(item.key)) {
                itemsMap.set(item.key, item);
            }
        });
    });

    mergedRefinement.items = Array.from(itemsMap.values());

    return mergedRefinement;
};

const mapFacetValues = (facetValues, refinement) => {
    if (!refinement?.items || !Array.isArray(facetValues)) {
        return [];
    }

    return facetValues.map(facet => refinement.items.find(item => item.key === facet)?.value).filter(Boolean);
};

export default {
    transformProductDataToRecsServiceFormat,
    getPdpCompareAttributes,
    findRefinementInMasterList,
    mapFacetValues
};
